import { useTheme } from "@mui/material/styles";
import AppBar from "@mui/material/AppBar/AppBar";
import Box from "@mui/material/Box/Box";
import Toolbar from "@mui/material/Toolbar/Toolbar";
import { useAuth } from "@sal/authentication.react";
import { AuthenticatedUserMenu } from "@sal/portal";
import { VerticalNavigation } from "../../components/VerticalNavigation";
import { Outlet, useNavigate } from "react-router-dom";
import { SecurityContext } from "../../constants";
import { type LayoutProps } from ".";
import { QuickSearch } from "../../components/QuickSearch";
import { Button, IconButton, Typography } from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import { getTerm, UserOrganisationToggle, VerticalNavigationComponentProps } from "@sal/onevent-portal";
import { Add } from "@mui/icons-material";

type Auth = ReturnType<typeof useAuth>;

function VerticalNavigationActionButton({ collapsed }: VerticalNavigationComponentProps) {
	const theme = useTheme();
	const buttonPath = "/Order";

	return (
		<Box
			display="flex"
			alignItems="center"
			justifyContent="center"
			width="100%"
			paddingX={collapsed ? 0 : 6}
			marginTop={4}
		>
			<Button
				variant="contained"
				color="primary"
				fullWidth={!collapsed}
				title={getTerm("navigation_NewOrder")}
				sx={{
					paddingY: collapsed ? theme.spacing(2) : theme.spacing(3),
					minWidth: collapsed ? "auto" : "initial",
					borderRadius: theme.shape.borderRadius * 2
				}}
				onClick={() => (window.location.href = `/#${buttonPath}`)}
			>
				<Typography
					fontSize={collapsed ? "large" : "0.85rem"}
					color={theme.palette.common.white}
					sx={{ display: "flex", alignItems: "center", gap: 1 }}
				>
					<Add fontSize="inherit" />
					{!collapsed && <span>{getTerm("navigation_NewOrder")}</span>}
				</Typography>
			</Button>
		</Box>
	);
}

export function VerticalLayout({ menuItems }: LayoutProps) {
	const auth = useAuth();
	const theme = useTheme();
	const navigate = useNavigate();

	/* Permissions; */
	const hasUserMenuPermission = (auth: Auth) => auth.userHasPermission(SecurityContext.Host, "User_Menu");
	const hasCreateOrderPermission = (auth: Auth) =>
		auth.userHasPermission("apI_ContainerTransportImportFCLOrder", "Write") ||
		auth.userHasPermission("apI_ContainerTransportImportFAKOrder", "Write") ||
		auth.userHasPermission("apI_ContainerTransportExportFCLOrder", "Write") ||
		auth.userHasPermission("apI_ContainerTransportExportFAKOrder", "Write");
	/* Permissions; */

	const handleSettingIconClick = () => {
		navigate("MyAccount");
	};

	return (
		<>
			{auth.isLoggedIn && (
				<Box flexGrow={0}>
					<VerticalNavigation
						width={{
							expanded: 250,
							collapsed: 75
						}}
						clientLogo={{
							expanded: "/images/move_logo.svg",
							collapsed: "/images/move_icon_logo.svg"
						}}
						clientLogoStyle={{
							expanded: {
								height: "3rem"
							}
						}}
						MenuProps={{
							items: menuItems,
							menuItemsStyles: {
								color: theme.palette.primary.main,
								opacity: 0.8
							}
						}}
						components={{
							Header: hasCreateOrderPermission(auth) ? VerticalNavigationActionButton : undefined
						}}
					/>
				</Box>
			)}
			{/* Set minWidth=0, to allow datagrid layouts with flex layout columns */}
			{/* https://github.com/mui/mui-x/issues/8895#issuecomment-1537134652 */}
			<Box flexGrow={1} minWidth={0} display="flex" flexDirection="column">
				{auth.isLoggedIn && (
					<Box flexGrow={0}>
						<AppBar
							position="static"
							sx={{
								backgroundColor: theme.palette.background.default
							}}
						>
							<Toolbar
								disableGutters
								sx={{
									alignItems: "stretch",
									paddingX: theme.spacing(8)
								}}
							>
								<Box display="flex" width="100%" alignItems="center" justifyContent="flex-end">
									<Box
										display="flex"
										flexGrow="1"
										alignSelf="stretch"
										alignItems={"center"}
										color={theme.palette.text.primary}
										sx={{
											borderRadius: "24px"
										}}
									>
										<Box
											width="100%"
											minWidth="200px"
											maxWidth="500px"
											pr={5}
											sx={{
												borderRadius: "24px"
											}}
										>
											<QuickSearch />
										</Box>
									</Box>
									<Box
										flexGrow={0}
										display="flex"
										alignItems="center"
										alignSelf="stretch"
										color={theme.palette.grey[600]}
										gap={4}
										sx={{
											fontWeight: 400
										}}
									>
										<Box
											alignItems="center"
											minWidth="300px"
											maxWidth="400px"
											sx={{
												"& .MuiOutlinedInput-root": {
													"& fieldset": {
														border: "none"
													}
												},
												"& .MuiTextField-root": {
													borderRadius: "24px",
													padding: "4px 8px !important",
													fontSize: "0.85rem"
												}
											}}
										>
											<UserOrganisationToggle />
										</Box>
										{/* {hasUserMenuPermission(auth) && <AuthenticatedUserMenu />} */}
										<AuthenticatedUserMenu />
										{/*TODO: Remove this once user menu permissions are implemented*/}
									</Box>
									<IconButton color="inherit" onClick={handleSettingIconClick}>
										<SettingsIcon sx={{ color: theme.palette.grey[600] }} />
									</IconButton>
								</Box>
							</Toolbar>
						</AppBar>
					</Box>
				)}
				<Box
					flexGrow={1}
					overflow="auto"
					sx={{
						scrollbarGutter: "stable"
					}}
				>
					<Outlet />
				</Box>
			</Box>
		</>
	);
}
